import { toCsv } from '../../../lib/csv';
import { listenerLabel } from '../../../lib/listenerName';
import type { DayView, ReportItem } from './employmentTypes';

/** Seconds → hours with two decimals ("7.50"). */
export const hours = (seconds: number | null | undefined): string =>
  seconds === null || seconds === undefined ? '' : (seconds / 3600).toFixed(2);

/** Paise digit string → "1234.50"; redacted (null) → ''. */
export const rupeesCell = (paise: string | null | undefined): string => {
  if (paise === null || paise === undefined || paise === '') return '';
  const p = BigInt(paise);
  const neg = p < 0n;
  const abs = neg ? -p : p;
  return `${neg ? '-' : ''}${(abs / 100n).toString()}.${(abs % 100n).toString().padStart(2, '0')}`;
};

/** One employee's attendance days (the Attendance table) as CSV. */
export function attendanceCsv(rows: DayView[], listener?: string): string {
  return toCsv(
    [
      ...(listener !== undefined ? ['listener'] : []),
      'date', 'day_type', 'status', 'frozen', 'scheduled_hours', 'presence_hours', 'break_hours', 'worked_hours',
      'regular_hours', 'overtime_hours', 'overtime_status', 'unpaid_extra_hours', 'outside_window_hours', 'missing_hours', 'adjustment_hours',
      'base_rupees', 'overtime_rupees', 'pending_overtime_rupees', 'total_rupees', 'wallet_status', 'credited_rupees', 'pending_credit_rupees',
    ],
    rows.map((d) => [
      ...(listener !== undefined ? [listener] : []),
      d.workDate,
      d.dayType,
      d.status,
      d.frozen ? 'yes' : 'no',
      hours(d.scheduledSeconds),
      hours(d.presenceSeconds),
      hours(d.breakDeductedSeconds),
      hours(d.workedSeconds),
      hours(d.regularSeconds),
      hours(d.overtimeSeconds),
      d.overtimeStatus,
      hours(d.unpaidExtraSeconds),
      hours(d.outsideWindowSeconds ?? 0),
      hours(d.missingSeconds),
      hours(d.adjustmentSeconds),
      rupeesCell(d.basePaise),
      rupeesCell(d.overtimePaise),
      rupeesCell(d.pendingOvertimePaise),
      d.basePaise === null || d.overtimePaise === null ? '' : rupeesCell((BigInt(d.basePaise) + BigInt(d.overtimePaise)).toString()),
      d.settlementStatus ?? 'none',
      rupeesCell(d.settledPaise),
      rupeesCell(d.pendingSettlementPaise),
    ]),
  );
}

const who = (r: ReportItem) => [
  listenerLabel({ displayName: r.listener.displayName, handle: r.listener.handle, id: r.listener.id }),
  `@${r.listener.handle}`,
  r.listener.id,
  r.employment.employeeCode ?? '',
  r.employment.employmentType,
  r.employment.status,
];
const WHO = ['listener', 'handle', 'listener_id', 'employee_code', 'employment_type', 'employment_status'];

/** Cross-employee attendance totals for the period. */
export function employmentAttendanceCsv(rows: ReportItem[]): string {
  return toCsv(
    [...WHO, 'days', 'scheduled_hours', 'presence_hours', 'worked_hours', 'regular_hours', 'overtime_hours', 'unpaid_extra_hours', 'missing_hours'],
    rows.map((r) => [
      ...who(r), r.days, hours(r.scheduledSeconds), hours(r.presenceSeconds), hours(r.workedSeconds),
      hours(r.regularSeconds), hours(r.overtimeSeconds), hours(r.unpaidExtraSeconds), hours(r.missingSeconds),
    ]),
  );
}

/** Cross-employee ₹ for the period (money columns blank when redacted). */
export function employmentEarningsCsv(rows: ReportItem[]): string {
  return toCsv(
    [...WHO, 'salary_model', 'work_time_basis', 'schedule_type', 'settlement_mode', 'days', 'worked_hours', 'regular_hours', 'overtime_hours', 'base_rupees', 'overtime_rupees', 'total_rupees'],
    rows.map((r) => [
      ...who(r), r.terms?.salaryModel ?? '', r.terms?.workTimeBasis ?? '', r.terms?.scheduleType ?? '', r.terms?.settlementMode ?? '', r.days,
      hours(r.workedSeconds), hours(r.regularSeconds), hours(r.overtimeSeconds),
      rupeesCell(r.basePaise), rupeesCell(r.overtimePaise), rupeesCell(r.employmentTotalPaise),
    ]),
  );
}

/** Employees with overtime in the period. */
export function employmentOvertimeCsv(rows: ReportItem[]): string {
  return toCsv(
    [...WHO, 'days', 'scheduled_hours', 'worked_hours', 'overtime_hours', 'unpaid_extra_hours', 'overtime_rupees'],
    rows
      .filter((r) => r.overtimeSeconds > 0 || r.unpaidExtraSeconds > 0)
      .map((r) => [...who(r), r.days, hours(r.scheduledSeconds), hours(r.workedSeconds), hours(r.overtimeSeconds), hours(r.unpaidExtraSeconds), rupeesCell(r.overtimePaise)]),
  );
}

/** Σ credited / pending settlement paise per employment (from the settlements list). */
export type SettledByEmployment = Record<string, { credited: string | null; pending: string | null }>;

/**
 * One line per employee, ready for a payroll sheet. With `settled` (the
 * period's settlement rows, summed per employment) it also says how much of
 * the total already reached the wallet and how much still waits.
 */
export function employmentPayrollCsv(rows: ReportItem[], from: string, to: string, settled?: SettledByEmployment): string {
  return toCsv(
    [
      ...WHO, 'employment_id', 'period_from', 'period_to', 'timezone', 'salary_model', 'schedule_type', 'settlement_mode', 'settlement_trigger', 'days',
      'regular_hours', 'overtime_hours', 'missing_hours', 'base_rupees', 'overtime_rupees', 'total_rupees', 'credited_to_wallet_rupees', 'pending_wallet_credit_rupees',
    ],
    rows.map((r) => [
      ...who(r), r.employment.id, from, to, r.terms?.timezone ?? '', r.terms?.salaryModel ?? '', r.terms?.scheduleType ?? '', r.terms?.settlementMode ?? '', r.terms?.settlementTrigger ?? '', r.days,
      hours(r.regularSeconds), hours(r.overtimeSeconds), hours(r.missingSeconds),
      rupeesCell(r.basePaise), rupeesCell(r.overtimePaise), rupeesCell(r.employmentTotalPaise),
      rupeesCell(settled?.[r.employment.id]?.credited), rupeesCell(settled?.[r.employment.id]?.pending),
    ]),
  );
}

/** Settlement rows → Σ credited / Σ pending per employment (null when any amount is redacted). */
export function sumSettlements(items: Array<{ employmentId: string; status: string; amountPaise: string | null }>): SettledByEmployment {
  const out: Record<string, { credited: bigint | null; pending: bigint | null }> = {};
  for (const s of items) {
    const e = (out[s.employmentId] ??= { credited: 0n, pending: 0n });
    const key = s.status === 'credited' ? 'credited' : s.status === 'pending' ? 'pending' : null;
    if (!key) continue;
    e[key] = s.amountPaise === null || e[key] === null ? null : e[key] + BigInt(s.amountPaise);
  }
  return Object.fromEntries(
    Object.entries(out).map(([k, v]) => [k, { credited: v.credited === null ? null : v.credited.toString(), pending: v.pending === null ? null : v.pending.toString() }]),
  );
}
